import { useEffect, useMemo, useState } from 'react';

// Match = beide wollen dieselbe Immobilie → Herzen, Feier, Häuser, Handschlag.
const EMOJIS = ['💛', '🎉', '🏠', '🤝', '✨', '💛', '🎊', '🔑'];
const DURATION_MS = 3200;

interface Drop {
  left: number;
  delay: number;
  dur: number;
  emoji: string;
  size: number;
}

/**
 * Kurze Feier-Animation, wenn ein Swipe einen MATCH vervollständigt (beide Logins Hot).
 * Analog zu GoldRain, aber eigene Trigger-Achse: feuert imperativ aus der Server-Antwort
 * (matched:true) — `trigger` ist ein Zeitstempel/Zähler, damit auch dieselbe Karte erneut
 * feuern kann. Blockiert das Swipen nicht (pointer-events: none), verschwindet nach ~3 s.
 */
export function MatchRain({ trigger }: { trigger: number | undefined }): JSX.Element | null {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (trigger === undefined) return;
    setActive(true);
    const t = setTimeout(() => setActive(false), DURATION_MS);
    return () => clearTimeout(t);
  }, [trigger]);

  const drops = useMemo<Drop[]>(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.9,
      dur: 1.7 + Math.random() * 1.5,
      emoji: EMOJIS[i % EMOJIS.length] ?? '💛',
      size: 18 + Math.random() * 22,
    }));
  }, [trigger]);

  if (!active) return null;

  return (
    <div
      aria-hidden
      style={{ position: 'fixed', inset: 0, zIndex: 200, pointerEvents: 'none', overflow: 'hidden' }}
    >
      <style>{`
        @keyframes match-fall {
          0% { transform: translateY(-14vh) rotate(0deg); opacity: 0; }
          8% { opacity: 1; }
          100% { transform: translateY(114vh) rotate(360deg); opacity: 1; }
        }
        @keyframes match-pop {
          0% { transform: scale(0.4) translateY(14px); opacity: 0; }
          20% { transform: scale(1.14) translateY(0); opacity: 1; }
          80% { transform: scale(1) translateY(0); opacity: 1; }
          100% { transform: scale(0.98) translateY(-6px); opacity: 0; }
        }
      `}</style>
      {drops.map((d, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            top: 0,
            left: `${d.left}%`,
            fontSize: d.size,
            animation: `match-fall ${d.dur}s linear ${d.delay}s forwards`,
          }}
        >
          {d.emoji}
        </span>
      ))}
      <div style={{ position: 'absolute', top: '28%', left: 0, right: 0, textAlign: 'center' }}>
        <div
          style={{
            display: 'inline-block',
            animation: 'match-pop 3.2s ease forwards',
            background: 'linear-gradient(135deg, #fde68a 0%, #fb7185 55%, #e11d48 100%)',
            color: '#4c0519',
            fontWeight: 900,
            padding: '12px 26px',
            borderRadius: 18,
            boxShadow: '0 12px 38px rgba(225,29,72,0.5)',
            fontSize: '1.5rem',
            letterSpacing: '-0.02em',
          }}
        >
          🎯 It's a Match!
          <div style={{ fontSize: '0.85rem', fontWeight: 700, opacity: 0.9, marginTop: 2 }}>
            Ihr wollt beide diese Immobilie — jetzt Kontakt aufnehmen 🤝
          </div>
        </div>
      </div>
    </div>
  );
}
